(function() {
  'use strict';

  if (customElements.get('card-variants')) {
    return;
  }

  class CardVariants extends HTMLElement {
    constructor() {
      super();
      this.card = this.closest('.card-wrapper');
    }

    connectedCallback() {
      this.initializeSelection();
      this.bindEvents();
    }

    initializeSelection() {
      this.updateVariantAvailability();
      
      const optionGroups = this.querySelectorAll('.card-variants__group');
      
      optionGroups.forEach(group => {
        const swatches = group.querySelectorAll('.card-variant-swatch');
        const hasSelection = Array.from(swatches).some(s => 
          s.classList.contains('card-variant-swatch--selected')
        );
        
        if (!hasSelection && swatches.length > 0) {
          const firstAvailable = Array.from(swatches).find(s => 
            !s.classList.contains('card-variant-swatch--disabled')
          );
          const firstSwatch = firstAvailable || swatches[0];
          if (firstSwatch) {
            firstSwatch.classList.add('card-variant-swatch--selected');
          }
        }
      });

      const selectedVariant = this.getSelectedVariant();
      if (selectedVariant) {
        this.updateCardWithVariant(selectedVariant);
      }
      this.updateQuickAddForm(selectedVariant);
      this.updateQuickAddModalUrl(selectedVariant);
    }

    bindEvents() {
      this.addEventListener('click', (e) => {
        const swatch = e.target.closest('.card-variant-swatch');
        if (!swatch) return;
        
        e.preventDefault();
        e.stopPropagation();
        
        swatch.blur();
        
        this.handleSwatchClick(swatch);
      });
    }

    handleSwatchClick(swatch) {
      const optionType = swatch.dataset.optionType;

      if (optionType) {
        this.querySelectorAll(`[data-option-type="${optionType}"]`).forEach(s => {
          s.classList.remove('card-variant-swatch--selected');
        });
      }
      swatch.classList.add('card-variant-swatch--selected');

      const selectedVariant = this.getSelectedVariant();
      
      if (selectedVariant) {
        this.updateCardWithVariant(selectedVariant);
      }

      this.updateQuickAddForm(selectedVariant);
      this.updateQuickAddModalUrl(selectedVariant);
      
      this.updateVariantAvailability();
    }

    getSelectedVariant() {
      if (!this.card) return null;

      const productId = this.getProductId();
      const selectedOptions = this.getSelectedOptions();
      
      return this.findVariantByOptions(productId, selectedOptions);
    }

    getProductId() {
      const variantsScript = this.querySelector('script[data-product-variants]');
      const productId = variantsScript ? variantsScript.dataset.productVariants : null;
      return productId;
    }

    getSelectedOptions() {
      const selectedOptions = [null, null, null];
      const optionGroups = this.querySelectorAll('.card-variants__group');
      
      optionGroups.forEach((group, index) => {
        const selectedSwatch = group.querySelector('.card-variant-swatch--selected');
        if (selectedSwatch) {
          selectedOptions[index] = selectedSwatch.dataset.optionValue;
        }
      });
      
      return selectedOptions;
    }

    findVariantByOptions(productId, selectedOptions) {
      let variantsScript = this.querySelector(`script[data-product-variants="${productId}"]`);
      
      if (!variantsScript) {
        variantsScript = document.querySelector(`script[data-product-variants="${productId}"]`);
      }
      
      if (!variantsScript) {
        return null;
      }

      try {
        const variantsData = JSON.parse(variantsScript.textContent);
        const matches = (variant) => selectedOptions.every((selectedOption, index) => {
          const variantOption = variant[`option${index + 1}`];
          if (selectedOption === null) return true;
          return variantOption === selectedOption;
        });

        const matchingVariants = variantsData.variants.filter(matches);
        return this.getPreferredVariant(matchingVariants, selectedOptions);
      } catch (error) {
        console.error('Error parsing variants data:', error);
        return null;
      }
    }

    getPreferredVariant(matchingVariants, selectedOptions) {
      if (!Array.isArray(matchingVariants) || !matchingVariants.length) {
        return null;
      }

      // Exact option match (or only one candidate): keep it even when sold out,
      // so the ATC button can mirror PDP sold-out state.
      if (matchingVariants.length === 1) {
        return matchingVariants[0];
      }

      const selectedCount = selectedOptions.filter(optionValue => optionValue !== null).length;
      const optionGroupCount = this.querySelectorAll('.card-variants__group').length;
      const isCompleteSelection = optionGroupCount > 0 && selectedCount >= optionGroupCount;

      if (isCompleteSelection) {
        return matchingVariants[0];
      }

      const availableVariants = matchingVariants.filter(variant => variant.available === true);
      const variantsPool = availableVariants.length ? availableVariants : matchingVariants;

      return variantsPool.reduce((cheapestVariant, currentVariant) => {
        const cheapestPrice = Number(cheapestVariant?.price);
        const currentPrice = Number(currentVariant?.price);

        if (Number.isNaN(currentPrice)) {
          return cheapestVariant;
        }

        if (Number.isNaN(cheapestPrice) || currentPrice < cheapestPrice) {
          return currentVariant;
        }

        return cheapestVariant;
      }, variantsPool[0]);
    }

    updateCardWithVariant(variant) {
      if (!this.card || !variant) return;

      this.updateCardImage(variant);
      this.updateCardPrice(variant);
      this.updateCardAvailability(variant);
    }

    updateCardImage(variant) {
      if (!this.card) return;

      const imageElement = this.card.querySelector('[data-component="card-image"]');
      
      if (!imageElement || !variant.featured_image) return;

      const featured_image_src = variant.featured_image;
      const featured_image_srcset = [140, 165, 180, 320, 340, 360, 380, 420, 460, 500, 533, 720, 940, 1066].map(w => `${featured_image_src.replace(/width=\d+/, `width=${w}`)} ${w}w`).join(', ');;

      imageElement.src = featured_image_src;
      imageElement.srcset = featured_image_srcset;

    }

    updateCardPrice(variant) {
      if (!this.card) return;

      const priceContainer = this.card.querySelector('.card__price');
      if (!priceContainer || variant.price === undefined) return;

      const fromPrice = priceContainer.getAttribute('data-from-price');
      const priceElement = priceContainer.querySelector('.price');
      const formattedPrice = variant.price_formatted || this.formatPrice(variant.price);
      
      const regularPrice = priceContainer.querySelector('.price__regular .price-item--regular');
      if (regularPrice) {
        // regularPrice.textContent = formattedPrice;
        regularPrice.textContent = fromPrice ? `${fromPrice} ${formattedPrice}` : formattedPrice;
      }
      
      const salePrice = priceContainer.querySelector('.price__sale .price-item--sale');
      const comparePriceElement = priceContainer.querySelector('.price__sale .price-item__original');

      const compareAtPrice = typeof variant.compare_at_price === 'number' ? variant.compare_at_price : parseFloat(variant.compare_at_price);
      const hasCompareAtPrice = compareAtPrice && compareAtPrice > variant.price;
      const formattedCompareAtPrice = variant.compare_at_price_formatted || this.formatPrice(compareAtPrice);

      if (priceElement) {
        priceElement.classList.toggle('price--on-sale', hasCompareAtPrice);
        priceElement.classList.toggle('price--sold-out', variant.available === false);
        priceElement.classList.toggle('price--free', Number(variant.price) === 0);
        if (!hasCompareAtPrice) {
          priceElement.classList.remove('price--on-sale');
        }
      }

      if (salePrice) {
        // salePrice.textContent = hasCompareAtPrice ? formattedPrice : '';
        salePrice.textContent = hasCompareAtPrice ? (fromPrice ? `${fromPrice} ${formattedPrice}` : formattedPrice) : '';
      }

      if (comparePriceElement) {
        comparePriceElement.textContent = hasCompareAtPrice ? formattedCompareAtPrice : '';
      }

      const unitPriceWrapper = priceContainer.querySelector('.unit-price');
      if (unitPriceWrapper) {
        const measurement = variant.unit_price_measurement;
        const hasUnitPrice = Boolean(
          measurement &&
          variant.unit_price !== null &&
          variant.unit_price !== undefined
        );

        unitPriceWrapper.classList.toggle('hidden', !hasUnitPrice);

        if (hasUnitPrice) {
          const unitPriceValueElement = unitPriceWrapper.querySelector('.price-item--last > span:first-child');
          const unitPriceUnitElement = unitPriceWrapper.querySelector('.price-item--last > span:last-child');

          if (unitPriceValueElement) {
            unitPriceValueElement.textContent = this.formatPrice(variant.unit_price);
          }

          if (unitPriceUnitElement) {
            const referenceValue = measurement.reference_value && measurement.reference_value !== 1
              ? String(measurement.reference_value)
              : '';
            unitPriceUnitElement.textContent = `${referenceValue}${measurement.reference_unit || ''}`;
          }
        }
      }
    }

    updateCardAvailability(variant) {
      if (!this.card) return;

      if (variant.available === false) {
        this.card.classList.add('card--unavailable');
      } else {
        this.card.classList.remove('card--unavailable');
      }
    }

    updateQuickAddForm(variant) {
      if (!this.card) return;

      const productForm = this.card.querySelector('product-form');
      const quickAddForm = productForm?.querySelector('form');
      if (quickAddForm) {
        const variantInput = quickAddForm.querySelector('[name="id"]');
        const submitButton = quickAddForm.querySelector('[type="submit"]');

        if (productForm) {
          delete productForm.dataset.cartAddErrorVariantId;
          delete productForm.dataset.cartAddErrorMessage;
        }

        if (variant && variantInput) {
          variantInput.value = variant.id;
          variantInput.disabled = variant.available === false;

          if (submitButton) {
            this.updateSubmitButtonState(submitButton, variant.available !== false);
          }
        } else {
          if (variantInput) {
            variantInput.value = '';
            variantInput.disabled = true;
          }

          if (submitButton) {
            this.updateSubmitButtonState(submitButton, false);
          }
        }
      }
    }

    updateSubmitButtonState(submitButton, isAvailable) {
      submitButton.classList.remove('loading');

      const spinner = submitButton.querySelector('.loading__spinner');
      if (spinner) {
        spinner.classList.add('hidden');
      }

      const soldOutMessage = submitButton.querySelector('.sold-out-message');
      if (soldOutMessage) {
        soldOutMessage.classList.add('hidden');
      }

      const quickAddIcon = submitButton.querySelector('.quick-add__submit-icon');
      if (quickAddIcon) {
        quickAddIcon.classList.remove('hidden');
      }

      const buttonText = submitButton.querySelector('.button__text, .quick-add__text');
      const addToCartLabel = window.variantStrings?.addToCart || 'Add to cart';
      const soldOutLabel = window.variantStrings?.soldOut || 'Sold out';

      if (buttonText) {
        buttonText.textContent = isAvailable ? addToCartLabel : soldOutLabel;
      }

      if (isAvailable) {
        submitButton.disabled = false;
        submitButton.removeAttribute('aria-disabled');
        return;
      }

      submitButton.disabled = true;
      submitButton.setAttribute('aria-disabled', 'true');
    }

    updateQuickAddModalUrl(variant) {
      if (!this.card) return;

      const quickAddOpeners = this.card.querySelectorAll('button[data-product-url][aria-haspopup="dialog"]');
      if (!quickAddOpeners.length) return;

      quickAddOpeners.forEach((opener) => {
        const currentUrl = opener.getAttribute('data-product-url');
        if (!currentUrl) return;

        const baseProductUrl = opener.dataset.baseProductUrl || currentUrl;
        opener.dataset.baseProductUrl = baseProductUrl;

        let nextUrl = baseProductUrl;
        if (variant && variant.id) {
          try {
            const resolvedUrl = new URL(baseProductUrl, window.location.origin);
            resolvedUrl.searchParams.set('variant', variant.id);
            nextUrl = `${resolvedUrl.pathname}${resolvedUrl.search}`;
          } catch (error) {
            nextUrl = `${baseProductUrl}${baseProductUrl.includes('?') ? '&' : '?'}variant=${variant.id}`;
          }
        }

        opener.setAttribute('data-product-url', nextUrl);
      });
    }

    updateVariantAvailability() {
      const productId = this.getProductId();
      if (!productId) return;

      let variantsScript = this.querySelector(`script[data-product-variants="${productId}"]`);
      if (!variantsScript) {
        variantsScript = document.querySelector(`script[data-product-variants="${productId}"]`);
      }
      if (!variantsScript) return;

      try {
        const variantsData = JSON.parse(variantsScript.textContent);
        const currentSelections = this.getSelectedOptions();

        const optionGroups = this.querySelectorAll('.card-variants__group');
        
        optionGroups.forEach((group, groupIndex) => {
          const swatches = group.querySelectorAll('.card-variant-swatch');
          
          swatches.forEach(swatch => {
            const optionValue = swatch.dataset.optionValue;
            
            let hasAvailableVariant;
            
            if (groupIndex === 0) {
              hasAvailableVariant = variantsData.variants.some(variant => {
                return variant[`option${groupIndex + 1}`] === optionValue && variant.available;
              });
            } else {
              hasAvailableVariant = variantsData.variants.some(variant => {
                if (variant[`option${groupIndex + 1}`] !== optionValue) return false;

                if (!variant.available) return false;
                
                return currentSelections.every((selectedValue, index) => {
                  if (index === groupIndex) return true;
                  if (selectedValue === null) return true;
                  return variant[`option${index + 1}`] === selectedValue;
                });
              });
            }

            if (hasAvailableVariant) {
              swatch.classList.remove('card-variant-swatch--disabled');
            } else {
              swatch.classList.add('card-variant-swatch--disabled');
            }
          });
        });
      } catch (error) {
        console.error('Error updating variant availability:', error);
      }
    }

    formatPrice(price) {
      const validPrice = parseFloat(price) || 0;
      
      if (window.Shopify && window.Shopify.formatMoney) {
        return window.Shopify.formatMoney(validPrice, window.theme?.moneyFormat || '{{amount}}');
      }
      
      const currency = window.Shopify?.currency?.active || 'USD';
      const locale = document.documentElement.lang || 'en-US';
      
      return new Intl.NumberFormat(locale, {
        style: 'currency',
        currency: currency
      }).format(validPrice / 100);
    }
  }

  customElements.define('card-variants', CardVariants);

})();